import type { AdNode } from "../../core/AdNode";
import type { AdNodeManager } from "../../core/AdNodeManager";

import { AdEnginePlugin, type Hooks } from "../../core/AdEnginePlugin";

import invariant from "tiny-invariant";
import { InfoEvent } from "common-types";

import { closeButton } from "./ui/CloseButton";
import type { AdEngine } from "../../core/AdEngine";
import { EventSubscriber } from "../../helpers/eventSubscriber";
import { pushTrack } from "utils";
import { updateSizesForCompanions } from "./helpers/updateSizesForCompanions";
import { isFanTakeoverHeight } from "./helpers/isFanTakeoverHeight";

/**
 * The height in pixels that the ad must be greater than to render the close button.
 *
 * If it's higher than 50px we render the close button and call it regular floor_adhesion otherwise we call it
 * a persistent floor_adhesion, we don't render the close button and we refresh the ad.
 *
 * @todo Should this be a config option?
 */
const PERSISTENT_FLOOR_ADHESION_AD_MIN_HEIGHT = 50; // in pixels

/**
 * FloorAdhesionPlugin is responsible for rendering and handling the close button for the floor
 * adhesion ad slot. The close button will be rendered when the first rendered ad is greater than
 * `50px` in height. For ads `50px` in height or less, the close button will not be rendered and the
 * slot will be fixed.
 */
export class FloorAdhesionPlugin extends AdEnginePlugin {
  private readonly floorAdhesionSlotName = "floor_adhesion";
  private containerRendered = false;
  private adEngine?: AdEngine;
  private adNodeManager?: AdNodeManager;
  // Reference to the rendered close Button in case we need to delete it later
  private closeButtonElement?: HTMLButtonElement;
  private readonly collapseEventSubscriber = new EventSubscriber<void>(1);
  private readonly closeButtonOnClickEventSubscriber =
    new EventSubscriber<void>(1);
  private observer?: ResizeObserver;
  /**
   * When true, this page load is a tentpole. This tells unblock to remove the `uap` and
   * `uap_c` targeting keyvalues.
   */
  private isTentpole = false;

  /**
   * Observes size changes of the ad element using ResizeObserver.
   * On each resize, calls `render` to update the floor adhesion layout
   * based on the current ad height.
   *
   * @param wrapper - The container element that wraps the ad.
   * @param adNode - The AdNode instance representing the slot.
   */
  private startObservingSizeChanges(wrapper: HTMLElement, adNode: AdNode) {
    const adElement = adNode.getElement();
    if (!adElement) return;

    this.observer = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.target !== adElement) return;

        const adHeight = entry.contentRect.height;
        this.render(wrapper, adNode, adHeight);
      });
    });
    this.observer.observe(adElement);
  }

  private getWrapperId(adNode: AdNode) {
    return adNode?.getSlotConfig().dynamic?.single?.wrapper?.id;
  }

  private getFloorAdhesionWrapper(adNode: AdNode) {
    const floorAdhesionWrapperId = this.getWrapperId(adNode);
    invariant(floorAdhesionWrapperId, "floorAdhesionWrapperId is not defined");

    const floorAdhesionWrapper = document.getElementById(
      floorAdhesionWrapperId,
    );
    invariant(floorAdhesionWrapper, "floorAdhesionWrapper is not defined");

    return floorAdhesionWrapper;
  }

  private createAndAppendCloseButton(
    floorAdhesionWrapper: HTMLElement,
    floorAdhesionAdNode: AdNode,
  ) {
    this.closeButtonElement = closeButton(() => {
      floorAdhesionWrapper.style.display = "none";
      const refreshValue = floorAdhesionAdNode.getRenderCount();

      this.adNodeManager!.removeAdNode(floorAdhesionAdNode);
      pushTrack(InfoEvent.SLOT_FORCE_CLOSE_INFO, {
        slotName: this.floorAdhesionSlotName,
        rv: refreshValue || undefined,
      });
      this.closeButtonOnClickEventSubscriber.notify();
    });
    floorAdhesionWrapper.appendChild(this.closeButtonElement);
  }

  /**
   * Removes the close button from the DOM
   */
  private removeCloseButton() {
    if (this.closeButtonElement) {
      this.closeButtonElement.remove();
      this.closeButtonElement = undefined;
    }
  }

  private isFirstRenderOfBigAd(adHeight: number) {
    return (
      !this.containerRendered &&
      adHeight > PERSISTENT_FLOOR_ADHESION_AD_MIN_HEIGHT
    );
  }

  private isRefreshToSmallerAd(adHeight: number) {
    return (
      this.containerRendered &&
      adHeight <= PERSISTENT_FLOOR_ADHESION_AD_MIN_HEIGHT &&
      !isFanTakeoverHeight(adHeight)
    );
  }

  private isRefreshToBiggerAd(adHeight: number) {
    return (
      this.containerRendered &&
      (adHeight > PERSISTENT_FLOOR_ADHESION_AD_MIN_HEIGHT ||
        isFanTakeoverHeight(adHeight)) &&
      !this.closeButtonElement
    );
  }

  /**
   * Renders the ad unit. Determines if the close button should be rendered based on ad height,
   * or if this is a refresh, should the close button be removed because the height is now below the threshold.
   *
   * Additionally, sends a tracking event whenever the close button is clicked.
   * @param floorAdhesionWrapper - The container element that wraps the ad.
   * @param floorAdhesionAdNode - The AdNode instance representing the slot.
   * @param adHeight - The height of the ad.
   */
  private render(
    floorAdhesionWrapper: HTMLElement,
    floorAdhesionAdNode: AdNode,
    adHeight: number,
  ) {
    if (!this.containerRendered) {
      floorAdhesionWrapper.classList.add("add-background");
    }

    if (this.isFirstRenderOfBigAd(adHeight)) {
      this.createAndAppendCloseButton(
        floorAdhesionWrapper,
        floorAdhesionAdNode,
      );
    } else if (this.isRefreshToSmallerAd(adHeight)) {
      this.removeCloseButton();
    } else if (this.isRefreshToBiggerAd(adHeight)) {
      this.createAndAppendCloseButton(
        floorAdhesionWrapper,
        floorAdhesionAdNode,
      );
    }

    this.containerRendered = true;
  }

  private collapse(floorAdhesionAdNode: AdNode) {
    const adElement = floorAdhesionAdNode.getElement();
    const floorAdhesionWrapper =
      this.getFloorAdhesionWrapper(floorAdhesionAdNode);

    floorAdhesionWrapper.style.display = "none";
    if (adElement) {
      adElement.style.height = "0px";
    }
    this.containerRendered = false;

    this.collapseEventSubscriber.notify();

    if (this.observer) {
      this.observer.disconnect();
      this.observer = undefined;
    }
  }

  /**
   * When Floor Adhesion ad is rendered by a collapse creative, the wrapper gets
   * `display: none` style added from the creative. In some conditions like
   * closing featured video, we need to "expand" the wrapper to make it visible
   * again, removing the `display: none" style.
   *
   * @param floorAdhesionAdNode The ad node for the floor adhesion slot.
   */
  private expand(floorAdhesionAdNode: AdNode) {
    const floorAdhesionWrapper =
      this.getFloorAdhesionWrapper(floorAdhesionAdNode);

    // Remove the `display: none` style to make the wrapper visible again.
    floorAdhesionWrapper.style.display = "";
  }

  private addSlotRenderEndedFloorAdhesionListener(adEngine: AdEngine) {
    adEngine.gptWrapper.addSlotEventListener(
      this.floorAdhesionSlotName,
      "slotRenderEnded",
      (event: googletag.events.SlotRenderEndedEvent) => {
        const adNode = this.adNodeManager?.getAdNodeByName(
          this.floorAdhesionSlotName,
        );
        // FloorAdhesion ad node is not defined and is likely already removed
        // from the DOM. We don't need to do anything.
        if (!adNode) return;

        if (event.isEmpty) {
          this.collapse(adNode);
          return;
        }

        if (!this.observer) {
          this.startObservingSizeChanges(
            this.getFloorAdhesionWrapper(adNode),
            adNode,
          );
        }
      },
    );
  }

  hooks: Hooks = {
    onAfterAdNodeManagerInit: (adNodeManager: AdNodeManager) => {
      this.adNodeManager = adNodeManager;
    },
    onAdEngineInit: (adEngine: AdEngine) => {
      this.adEngine = adEngine;

      this.addSlotRenderEndedFloorAdhesionListener(adEngine);
    },
  };

  api = {
    /**
     * Tell the floor adhesion that this page is a tentpole page, and when the ad is unblocked,
     * we should remove the `uap` and `uap_c` targeting from the key values so programmatic
     * ads can be served.
     *
     * This is essentially to bypass the collapse creatives that target floor_adhesion when `uap`
     * is not `none`. Once AE3 is fully implemented we should be able to remove this logic
     * completely.
     */
    activateTentpole: () => {
      this.isTentpole = true;
    },
    block: () => {
      try {
        invariant(this.adNodeManager, "adNodeManager is not defined");
        this.adNodeManager.blockSlot(this.floorAdhesionSlotName);
      } catch (e) {
        console.warn(`Could not block the slot: ${e}`);
      }
    },
    unblock: async () => {
      const { adNodeManager, gptWrapper } = this.adEngine ?? {};

      if (this.isTentpole && gptWrapper) {
        gptWrapper.setTargeting("uap", "none");
        gptWrapper.setTargeting("uap_c", "none");
      }

      adNodeManager?.unblockSlot(this.floorAdhesionSlotName);
      const adNode = adNodeManager?.getAdNodeByName(this.floorAdhesionSlotName);

      if (adNode) {
        this.expand(adNode);
        updateSizesForCompanions(adNode);
        await this.adEngine?.requestRender([adNode]);
      }
    },
    /**
     * Registers a callback function to be executed when the ad is collapsed.
     *
     * @param callback - A function to be called when the ad is collapsed.
     * @returns A function that can be called to unsubscribe the callback from the event.
     */
    onCollapse: (callback: () => void): (() => void) => {
      return this.collapseEventSubscriber.subscribe(callback);
    },
    /**
     * Registers a callback function to be executed when the close button is clicked.
     *
     * @param callback - A function to be called when the close button is clicked.
     * @returns A function that can be called to unsubscribe the callback from the event.
     */
    onClose: (callback: () => void): (() => void) => {
      return this.closeButtonOnClickEventSubscriber.subscribe(callback);
    },
  };
}
